// /criminal-record-translate/for/$country — TH country combo
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { TARGET_COUNTRIES, getTargetCountry } from "@/data/police-target-countries";
import { comboCountrySectionsTh } from "@/data/criminal-record-content";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Globe2, MessageCircle, Phone, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/criminal-record-translate/for/$country")({
  loader: ({ params }) => {
    if (!getTargetCountry(params.country)) throw notFound();
    return { slug: params.country };
  },
  head: ({ params }) => {
    const c = getTargetCountry(params.country);
    if (!c) return { meta: [{ title: ctaTitle("ไม่พบประเทศ", "/criminal-record-translate/for/$country", "th")}] };
    const url = `${SITE}/criminal-record-translate/for/${c.slug}`;
    const price = c.hague ? "4,500" : "6,200";
    const title = `แพ็กเกจ PCSC + แปล + รับรอง สำหรับยื่นที่${c.nameTh} | NYC Criminal Record`;
    const desc = `แพ็กเกจรวมตรวจประวัติอาชญากรรม + แปล + ${c.hague ? "Apostille (Hague)" : `MFA + ${c.embassyTh}`} สำหรับยื่น${c.nameTh} เริ่ม ฿${price} เสร็จ 15-25 วันทำการ · วีซ่า: ${c.visaTypes.slice(0, 3).join(", ")}`;
    return {
      meta: [
        { title: ctaTitle(title, "/criminal-record-translate/for/$country", "th") },
        { name: "description", content: ctaDesc(desc, "/criminal-record-translate/for/$country", "th")},
        { name: "keywords", content: `แปล Criminal Record ${c.nameTh}, แพ็กเกจ PCSC ${c.nameTh}, ตรวจประวัติ ${c.nameTh}, Police Clearance for ${c.nameEn}` },
        { property: "og:title", content: ctaTitle(title, "/criminal-record-translate/for/$country", "th")},
        { property: "og:description", content: ctaDesc(desc, "/criminal-record-translate/for/$country", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/criminal-record-translate/for/${c.slug}`, `/en/criminal-record-translate/for/${c.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `Thai Criminal Record bundle for ${c.nameEn}`,
              serviceType: "Police Clearance translation & legalization for visa",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: c.nameEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: c.hague ? 4500 : 6200, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: [
                { q: `${c.nameTh}เป็น Hague Member ไหม?`, a: c.hague ? "ใช่ — ใช้ Apostille ของไทย" : `ไม่ใช่ — ต้องรับรองที่ ${c.embassyTh}` },
                { q: `วีซ่าที่ต้องใช้?`, a: c.visaTypes.join(", ") },
                { q: `ราคาแพ็กเกจ?`, a: `เริ่ม ฿${price} รวมทุกขั้นตอน` },
                { q: `เวลา?`, a: `15-25 วันทำการ มาตรฐาน · Express 7-10 วัน` },
              ].map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "Criminal Record + แปล", item: `${SITE}/criminal-record-translate` },
                { "@type": "ListItem", position: 3, name: c.nameTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: ComboCountryPage,
});

function ComboCountryPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const c = getTargetCountry(slug)!;
  const sections = comboCountrySectionsTh(c);
  const related = TARGET_COUNTRIES.filter((x) => x.slug !== slug).slice(0, 9);
  const price = c.hague ? "4,500" : "6,200";

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/criminal-record-translate" className="hover:text-gold">Criminal Record + แปล</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{c.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Globe2 className="h-3.5 w-3.5" /> สำหรับ {c.nameTh} ({c.nameEn})
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            แพ็กเกจ PCSC + แปล + รับรอง สำหรับยื่นที่<span className="text-gold">{c.nameTh}</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            ครบทุกขั้นตอน · {c.hague ? "Apostille (Hague Member)" : `MFA + ${c.embassyTh}`} · เริ่ม ฿{price} · 15-25 วันทำการ
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800"><BadgeCheck className="h-3 w-3" /> Quotable</span>
          แพ็กเกจ Criminal Record (PCSC) + แปล + รับรอง สำหรับยื่นที่{c.nameTh} — เริ่ม ฿{price} เสร็จ 15-25 วันทำการ {c.hague ? "ใช้ Apostille ของ MFA ไทย ไม่ต้องผ่านสถานทูต" : `ผ่าน MFA + ${c.embassyTh}`} รับประกันการยอมรับ 99.6%
        </blockquote>

        <div className="mt-10"><PoliceSections sections={sections} /></div>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `${c.nameTh}เป็น Hague Member?`, a: c.hague ? "ใช่ — ใช้ Apostille" : `ไม่ใช่ — ใช้ ${c.embassyTh}` },
                { q: `วีซ่า?`, a: c.visaTypes.join(", ") },
                { q: `ราคา?`, a: `฿${price}` },
                { q: `เวลา?`, a: "15-25 วันทำการ" },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศปลายทางอื่นๆ</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/criminal-record-translate/for/$country" params={{ country: r.slug }} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <Globe2 className="h-4 w-4 text-amber-600" />
                <span className="font-semibold text-slate-800">{r.nameTh}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
